import { _ } from "~/i18n";
import { IMapyBoxItem, TNoteVersion } from "~/interfaces";
import { icon, node } from "~/util/dom";
import { createUserNote } from "./note";
import { TUserButtonType, createUserNoteBtn } from "./btn";
import { likeUpdate, trackUpdate } from "~/util/providers/mapybox";
import MyMapsEditNote from "../dialogs/mymaps-edit-note";
import Dialog from "../dialog";
import { checkNoteVersion } from "~/util/user-note";

interface IUserNote {
	folder?: {
		id: string;
		title: string;
	};
	data?: IMapyBoxItem;
	type?: TUserButtonType;
	enableNote?: boolean;
	isPremiumUser?: boolean;
	noteVersion?: TNoteVersion;
	onNoteUpdate?: (note: string) => void;
	onNoteOpen?: (container: HTMLDivElement) => void;
}

// zobrazeni slozky + dalsi radek varianty:
// - tlacitko pridat poznamku | poznamka - kde bude editace, smazani
export default class UserNoteWithFolder {
	_opts: IUserNote;

	_dom: {
		[key: string]: HTMLElement;
	};

	_editNote: MyMapsEditNote;

	_userNote: ReturnType<typeof createUserNote>;

	constructor(opts: IUserNote) {
		this._opts = {
			type: "detail",
			onNoteUpdate: () => {},
			onNoteOpen: () => {},
			enableNote: false,
			isPremiumUser: false,
			noteVersion: "version1",
			...opts,
		};
		this._dom = {};
		this._editNote = null;
		this._userNote = null;

		this._build();
		this._buildNote();
	}

	destroy() {
		this._dom.container.remove();
		this._editNote?.destroy();
		this._userNote?.destroy();
	}

	print() {
		this._userNote?.print();
	}

	_build() {
		const container = node("div", { className: "user-note-folderBtn" });
		const folderLine = node("div", { className: "user-note-folderBtn__folderLine" });
		const noteLine = node("div", { className: "user-note-folderBtn__noteLine" });

		if (this._opts.folder) {
			const folderPart = node("div", { className: "user-note-folderBtn__folderParts" });

			folderLine.append(
				icon("folder"),
				folderPart,
			);

			// domu/prace
			if (this._opts.data?.liketype && this._opts.data.liketype !== "like" && this._opts.data.liketype !== "link") {
				folderPart.textContent = this._opts.data.liketype === "home"
					? _("detail.content.mymapsplace.savedHome")
					: _("detail.content.mymapsplace.savedWork");
			// track - ulozeny ve vice slozkach
			} else if (this._opts.data?.type === "track" && !this._opts.data.parent_id) {
				folderPart.textContent = _("detail.content.mymapsplace.saved");
			} else {
				folderPart.textContent = `${_("detail.content.mymapsplace.savedInFolder")} `;

				const folderLink = node("a", { textContent: this._opts.folder.title, href: "#" });

				folderPart.append(folderLink);
				folderLink.addEventListener("click", event => {
					event.stopPropagation();
					event.preventDefault();

					this._openFolder();
				});
			}

			container.append(folderLine);
		}

		container.append(noteLine);

		this._dom.container = container;
		this._dom.noteLine = noteLine;
	}

	_buildNote() {
		this._dom.container.classList.remove("empty");

		if (this._opts.data && "liketype" in this._opts.data) {
			this._dom.noteLine.remove();

			if (!this._opts.folder) {
				this._dom.container.classList.add("empty");
			}

			return;
		}

		this._dom.noteLine.innerHTML = "";

		let isFilled = false;

		if (this._opts.enableNote) {
			const { data } = this._opts;

			if (data && data.description) {
				this._userNote = createUserNote({
					data,
					isPremiumUser: this._opts.isPremiumUser,
					noteVersion: this._opts.noteVersion,
					onEdit: () => {
						this._onNoteEdit();
					},
					onRemove: () => {
						this._onNoteRemoveDialog();
					},
					onNoteOpen: () => {
						this._opts.onNoteOpen(this._userNote.container);
					},
				});

				this._dom.noteLine.append(this._userNote.container);
				isFilled = true;
			} else if (this._opts.isPremiumUser || checkNoteVersion(this._opts.noteVersion, "version2")) {
				this._dom.noteLine.append(createUserNoteBtn({
					type: this._opts.type,
					isPremiumUser: this._opts.isPremiumUser,
					noteVersion: this._opts.noteVersion,
					onClick: () => {
						this._onNoteEdit();
					},
				}));
				isFilled = true;
			}
		}

		if (!isFilled && !this._opts.folder) {
			this._dom.container.classList.add("empty");
		}
	}

	_createEditNoteDialog(): Promise<{
		note: string;
		request: boolean;
	}> {
		return new Promise(resolve => {
			const dialog = new MyMapsEditNote({
				data: this._opts.data,
				onSave: note => {
					this._editNote = null;
					resolve({
						note,
						request: true,
					});
				},
				onCancel: () => {
					this._editNote = null;
					resolve({
						note: "",
						request: false,
					});
				},
				onClose: () => {
					this._editNote = null;
					resolve({
						note: "",
						request: false,
					});
				},
			});

			dialog.open();
			this._editNote = dialog;
		});
	}

	async _onNoteEdit() {
		const editData = await this._createEditNoteDialog();

		if (editData.request) {
			this._opts.data.description = editData.note;
			this._buildNote();
			this._opts.onNoteUpdate(editData.note);
		}
	}

	_onNoteRemoveDialog() {
		const dialog = new Dialog({
			title: _("note.removeNote"),
			className: "remove-note-dialog",
		});

		dialog.setBasic({
			closeTxt: "delete",
			bindEnter: true,
			cb: () => {
				this._onNoteRemove();
			},
		});
		dialog.open();
	}

	async _onNoteRemove() {
		await this._updateData("");
		this._opts.data.description = "";
		this._buildNote();
		this._opts.onNoteUpdate("");
	}

	_updateData(description: string) {
		if (this._opts.data.type === "track") {
			return trackUpdate(this._opts.data.id, { description });
		}

		return likeUpdate(this._opts.data.id, { description });
	}

	_openFolder() {
		// prechod do slozky
		Mapy.getComponent("mymaps").setState({
			mid: this._opts.folder.id,
			cat: "mista-trasy",
		});

		if (Mapy.getComponent("mymaps").activate()) {
			Mapy.getComponent("mymaps").deactivate();
		}

		Mapy.getComponent("mymaps").activate();
		Mapy.getComponent("mymaps").activeComponent(Mapy.getComponent("places"));

		Mapy.getComponent("history").save();
	}

	get container() {
		return this._dom.container;
	}
}
